import { Stack, Typography } from '@mui/material';
import { CentralAssortmentItemsMemberView } from './CentralAssortmentItemsMemberView';
import { CentralAssortmentFiltersMemberView } from './CentralAssortmentFiltersMemberView';
import { useCentralAssortmentMemberViewFilterOptions } from './useCentralAssortmentMemberViewFilterOptions';
import { useCentralAssortmentMemberViewItems } from './useCentralAssortmentMemberViewItems';
import { AssortmentItemMinimal } from '@retail/my-assortment/types';
import { useDisclosure } from '@shared/hooks';
import {
  ExportToExcelButton,
  FilterChipContainer,
  ToggleFiltersButton,
  ToggleFiltersToolbar,
} from '@retail/components';
import { TotalItemsLoader } from '@retail/products/components';
import { useExportCentralAssortmentToExcel } from '@retail/my-assortment/data-access';
import { TagHeading } from '@shared/components';
import { useMyAssortmentTFunction } from '@retail/my-assortment/i18n';
import { centralAssortmentsFilters } from '@retail/my-assortment/utils';
import { SubAssortmentBase } from '@retail/products/types';
import {
  usePermissionsForCurrentUser,
  useSelectedOrgUnit,
} from '@retail/app/stores/selected-context';

interface Props {
  includeTitle?: boolean;
  /** Hides the floating selection toolbar, e.g. when the selection is confirmed by a dialog instead */
  hideActionToolbar?: boolean;
  itemsChecked: AssortmentItemMinimal[];
  toggleAddCheckedItemsModal?: () => void;
  setItemsChecked: (items: AssortmentItemMinimal[]) => void;
  setItemToAdd?: (item?: AssortmentItemMinimal) => void;
}

export function CentralAssortmentMemberView({
  includeTitle,
  hideActionToolbar,
  itemsChecked,
  toggleAddCheckedItemsModal,
  setItemsChecked,
  setItemToAdd,
}: Props) {
  const t = useMyAssortmentTFunction();
  const { userCanRead } = usePermissionsForCurrentUser();
  const { appTenant } = useSelectedOrgUnit();
  const canReadSubAssortments = userCanRead('SUB_ASSORTMENTS');

  const { isOpen: showFilters, onToggle: toggleFilters } = useDisclosure(true);

  const { items, totalNumberOfItems, foundNumberOfItems, isFetching, isError } =
    useCentralAssortmentMemberViewItems();

  const { suppliers, productGroups, subAssortments } = useCentralAssortmentMemberViewFilterOptions({
    suspense: false,
  });

  const { exportToExcel, isLoading } = useExportCentralAssortmentToExcel({
    items,
    includeSubAssortments: canReadSubAssortments,
  });

  return (
    <Stack flexGrow={1} overflow="hidden">
      {includeTitle && (
        <Stack px={3} pt={2} flexDirection="row" justifyContent="space-between" alignItems="center">
          <TagHeading>
            <Typography variant="h4">{t('myAssortment.centralAssortments.central')}</Typography>
          </TagHeading>
          <ExportToExcelButton
            onClick={exportToExcel}
            loading={isLoading}
            disabled={!items.length}
          />
        </Stack>
      )}

      <ToggleFiltersToolbar sx={{ mb: 1, justifyContent: 'space-between' }}>
        <FilterChipContainer
          allowedKeys={
            appTenant.hasMainSupplier
              ? centralAssortmentsFilters
              : centralAssortmentsFilters.filter((key) => key !== 'fromMainSupplier')
          }
          filterOptions={{
            suppliers,
            productGroups,
            subAssortments: canReadSubAssortments
              ? subAssortments.map((assortment) => {
                  return assortment as SubAssortmentBase;
                })
              : undefined,
          }}
        />
        <Stack flexDirection="row" alignItems="flex-end" gap={1}>
          {!isError && (
            <TotalItemsLoader
              totalItems={totalNumberOfItems}
              itemsShowing={foundNumberOfItems}
              isFetching={isFetching}
            />
          )}
          <ToggleFiltersButton showFilters={showFilters} toggleFilters={toggleFilters} />
        </Stack>
      </ToggleFiltersToolbar>
      <Stack flexGrow={1} flexDirection="row" overflow="hidden">
        <CentralAssortmentItemsMemberView
          itemsChecked={itemsChecked}
          setItemsChecked={setItemsChecked}
          hideActionToolbar={hideActionToolbar}
          toggleAddCheckedItemsModal={toggleAddCheckedItemsModal}
          addItemToAssortment={setItemToAdd}
        />

        <CentralAssortmentFiltersMemberView showFilters={showFilters} />
      </Stack>
    </Stack>
  );
}
